/**
 * <VoiceBars> — la voix qui passe, en trois barres (atom).
 *
 * Sur une vignette du live, à côté du prénom, et sur le bouton du micro : des barres qui montent avec
 * la voix, comme l'ancienne vignette (`UsernameComponent`, weekly du 03/11 : « la waveform revient »).
 * Micro coupé, elles restent basses. Ce n'est pas `<LevelBars>` (le niveau d'un test du matériel, une
 * jauge) : ici les barres ondulent tant qu'il parle, plus amples quand il parle fort.
 *
 * Sans horloge : les hauteurs (0 à 1) arrivent en prop (`levels`), ou d'une source qui les pousse à son rythme
 * (`feed`, quelques fois par seconde) sans redessiner le composant. Jamais dans l'état de toute la salle.
 *
 * « Réduire les animations » demandé par l'appareil : les barres ne suivent plus la voix, elles disent seulement
 * qu'il parle (à mi-hauteur) ou non (au repos).
 */
import React, { useEffect } from "react"
import { StyleSheet as RNStyleSheet, View } from "react-native"
import Animated, { type SharedValue, useAnimatedStyle, useSharedValue } from "react-native-reanimated"
import { StyleSheet } from "react-native-unistyles"

import { useReduceMotion } from "../../hooks/useReduceMotion"
import { type SemanticColorKey } from "../../theme/tokens/colors"

/** Ce qui pousse les hauteurs des barres, à son rythme ; rend de quoi l'arrêter. */
export type VoiceBarsFeed = (push: (levels: readonly number[]) => void) => () => void

export type VoiceBarsProps = {
    /** Une hauteur par barre, de 0 (au repos) à 1 (toute la hauteur). Trois par défaut. */
    levels?: readonly number[]
    /**
     * Une source qui pousse les hauteurs (trois barres) : elles bougent sans redessiner le composant. La voix change
     * plusieurs fois par seconde, et redessiner chaque vignette à ce rythme coûtait le fil principal de la page.
     */
    feed?: VoiceBarsFeed
    /** La hauteur des barres, en px. Défaut 12, celle d'un prénom de vignette. */
    size?: number
    /** Défaut `success`, le vert de « il parle ». */
    color?: SemanticColorKey
    testID?: string
}

/** La barre au repos : assez haute pour se voir, basse pour ne rien dire. Une proportion de `size`. */
const REST = 0.25
/** Mouvement réduit : la barre de quelqu'un qui parle, fixe. */
const CALM_SPEAKING = 0.6
/**
 * Au-dessus, il parle : en mouvement réduit, chaque barre le dit seule. La salle ondule au-dessus de 0,2 tant qu'il
 * parle et tombe à 0 dès qu'il se tait (`engine/live/room/speakingBars`, son « parle » déjà lissé par LiveKit).
 */
const SPEAKING = 0.15
const BAR_COUNT = 3
const BAR_WIDTH = 3
const SILENT: readonly number[] = [0, 0, 0]

/**
 * La hauteur bouge plusieurs fois par seconde : elle ne passe jamais par une feuille Unistyles. Au web, chaque valeur
 * nouvelle d'un style y ajoute une règle et réécrit toute la feuille de l'app, puis la retire quand plus rien ne la
 * porte : mesuré sur la box, la page de l'élève passait de 1 à 34 % de son fil principal. Les styles de la barre
 * restent fixes, et sa hauteur est une échelle verticale posée par Reanimated (géométrie seule, sans jeton :
 * l'exception de la règle 1 du contrat, comme `<PinchZoom>`).
 */
export function VoiceBars(props: VoiceBarsProps) {
    const { levels = SILENT, feed, size = 12, color = "success", testID } = props
    const calm = useReduceMotion()
    const first = useSharedValue(0)
    const second = useSharedValue(0)
    const third = useSharedValue(0)
    useEffect(() => {
        if (feed == null) return undefined
        const shared = [first, second, third]
        return feed((next) => shared.forEach((value, index) => (value.value = next[index] ?? 0)))
    }, [feed, first, second, third])
    const count = feed != null ? BAR_COUNT : levels.length
    return (
        <View style={styles.row(size)} testID={testID} pointerEvents="none" accessibilityElementsHidden>
            {Array.from({ length: count }, (_, index) => (
                <Bar
                    key={index}
                    shared={feed != null ? [first, second, third][index] : undefined}
                    level={levels[index] ?? 0}
                    calm={calm}
                    color={color}
                />
            ))}
        </View>
    )
}

function Bar(props: { shared?: SharedValue<number>; level: number; calm: boolean; color: SemanticColorKey }) {
    const { shared, level, calm, color } = props
    const fixed = useSharedValue(level)
    useEffect(() => {
        fixed.value = level
    }, [level, fixed])
    const source = shared ?? fixed
    const height = useAnimatedStyle(() => {
        const raw = source.value
        const clamped = Number.isFinite(raw) ? Math.max(0, Math.min(1, raw)) : 0
        const shown = calm ? (clamped > SPEAKING ? CALM_SPEAKING : REST) : Math.max(REST, clamped)
        return { transform: [{ scaleY: shown }] }
    })
    return (
        <View style={styles.slot}>
            <Animated.View style={[RNStyleSheet.absoluteFill, height]}>
                <View style={styles.fill(color)} />
            </Animated.View>
        </View>
    )
}

const styles = StyleSheet.create((theme) => ({
    row: (size: number) => ({
        flexDirection: "row" as const,
        alignItems: "center" as const,
        gap: 2,
        height: size,
    }),
    slot: {
        width: BAR_WIDTH,
        height: "100%",
    },
    fill: (color: SemanticColorKey) => ({
        flex: 1,
        borderRadius: BAR_WIDTH / 2,
        backgroundColor: theme.colors[color],
    }),
}))
